<template>
  <v-dialog :value="value" @input="$emit('input', $event)" max-width="900" scrollable>
    <v-card>
      <v-card-title class="dialog-title">
        {{ $t('org-dashboard.efficiency-drilldown-title', { tier: activeTierLabel }) }}
        <v-spacer />
        <v-btn icon @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Tier tabs -->
        <v-tabs v-model="activeTab" background-color="transparent" class="drilldown-tabs" @change="onTabChange">
          <v-tab v-for="tab in tabs" :key="tab.key">
            <span class="tab-dot" :style="{ backgroundColor: tab.color }"></span>
            {{ tab.label }}
          </v-tab>
        </v-tabs>

        <!-- Data table -->
        <v-data-table
          :headers="tableHeaders"
          :items="items"
          :server-items-length="totalItems"
          :options.sync="internalOptions"
          :loading="loading"
          :no-data-text="$t('org-dashboard.efficiency-no-data')"
          :footer-props="{
            'items-per-page-options': [10, 20, 50],
          }"
          @update:options="onOptionsChange"
          class="drilldown-table"
        >
          <template v-slot:item.identifier="{ item }">
            <a class="drilldown-link" @click.stop="$emit('charger-click', item.id)">{{ item.identifier }}</a>
            <span v-if="item.sessions === 0" class="never-chip">{{ $t('org-dashboard.efficiency-never-badge') }}</span>
          </template>

          <template v-slot:item.energy="{ item }">
            {{ item.energy }} kWh
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
const TABS = [
  { key: 'high',   color: '#22c55e' },
  { key: 'normal', color: '#3b82f6' },
  { key: 'low',    color: '#f59e0b' },
  { key: 'never',  color: '#ef4444' },
]

export default {
  name: 'DashboardEfficiencyDrilldown',
  props: {
    value: { type: Boolean, default: false },
    tier: { type: String, default: 'low' },
    items: { type: Array, default: () => [] },
    totalItems: { type: Number, default: 0 },
    loading: { type: Boolean, default: false },
  },

  data() {
    return {
      activeTab: this.tierToTabIndex(this.tier),
      internalOptions: { page: 1, itemsPerPage: 20 },
    }
  },

  computed: {
    tabs() {
      return TABS.map(t => ({
        ...t,
        label: this.$t('org-dashboard.efficiency-tier-' + t.key),
      }))
    },

    activeTierLabel() {
      const tab = TABS[this.activeTab]
      return tab ? this.$t('org-dashboard.efficiency-tier-' + tab.key) : ''
    },

    tableHeaders() {
      return [
        { text: this.$t('org-dashboard.health-identifier'), value: 'identifier' },
        { text: this.$t('org-dashboard.health-location'), value: 'locationName' },
        { text: this.$t('org-dashboard.efficiency-sessions'), value: 'sessions' },
        { text: this.$t('org-dashboard.efficiency-energy'), value: 'energy' },
      ]
    },
  },

  watch: {
    tier(val) {
      this.activeTab = this.tierToTabIndex(val)
    },
  },

  methods: {
    tierToTabIndex(t) {
      const idx = TABS.findIndex(tab => tab.key === t)
      return idx >= 0 ? idx : 2
    },

    onTabChange(tabIndex) {
      const tab = TABS[tabIndex]
      if (tab && tab.key !== this.tier) {
        this.$emit('update:tier', tab.key)
      }
    },

    onOptionsChange(options) {
      this.internalOptions = options
      this.$emit('update:options', options)
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-title {
  font-size: 16px;
  font-weight: 700;
  border-bottom: 1px solid #e5e7eb;
}

.drilldown-tabs {
  border-bottom: 1px solid #e5e7eb;
}

.tab-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 6px;
}

.drilldown-table {
  ::v-deep .v-data-table__wrapper {
    max-height: 500px;
    overflow-y: auto;
  }
}

.drilldown-link {
  color: #1976d2;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: #1565c0;
    text-decoration-thickness: 2px;
  }
}

.never-chip {
  display: inline-block;
  font-size: 9px;
  font-weight: 600;
  color: #ef4444;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 4px;
  padding: 1px 4px;
  margin-left: 4px;
  vertical-align: middle;
}
</style>
